import { useState } from 'react';

export const useSupportPage = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = {
    'general': 'General',
    'billing': 'Billing',
    'technical': 'Technical'
  };

  const faqs = {
    general: [
      {
        question: '🚀 Where does my app get published?',
        answer: 'Your app is published on the Google Play Store under the elef89 developer account. You\'ll get a direct Play Store link to share with your users.'
      },
      {
        question: '👀 Who can see my app?',
        answer: 'That depends on the release mode:\n\n' +
                '• Internal Testing – Only invited testers\n\n' +
                '• Closed Testing – Limited group via email or link\n\n' +
                '• Production – Publicly available on Google Play\n\n' +
                'We\'ll help you choose the right mode when uploading.'
      },
      {
        question: '🔁 Can I upload a new version of my app later?',
        answer: 'Yes — you can upload updates anytime.\nNote: Fees for version updates depend on your pricing plan.'
      },
      {
        question: '💸 How much does it cost to update my app?',
        answer: 'Our current pricing for version updates is available inside the app. It includes the Play Store update process, rollout support, and QA checks (if needed).'
      },
      {
        question: '🔄 Can I transfer the app to my own Google Play account?',
        answer: 'Yes! If you have your own Google Play developer account, we can transfer ownership of your app to it.\n\nThere\'s a one-time fee for the transfer process. Just contact our support team when you\'re ready, and we\'ll guide you through it.'
      },
      {
        question: '🧪 Can I start with internal testing?',
        answer: 'Absolutely. Start privately with testers, and go public when you\'re ready — no need to re-upload the app.'
      },
      {
        question: '🔐 Is my app visible to the public during testing?',
        answer: 'No. Internal and closed testing modes are completely private. Only your testers will see the app.'
      },
      {
        question: '🧯 Can I remove my app from Google Play?',
        answer: 'Yes. You can ask us to unpublish the app at any time. It will no longer be available for download.'
      },
      {
        question: '⚖️ Who is responsible for the app\'s content?',
        answer: 'You are. Even though the app is published under the elef89 account, you\'re responsible for its functionality, legality, and content. We don\'t alter your app in any way.'
      }
    ],
    billing: [
      {
        question: '🔐 Is payment secure?',
        answer: 'Yes. All payments are processed securely via Stripe. Your payment details are encrypted and never stored by us.'
      },
      {
        question: '💼 What subscription plans do you offer?',
        answer: 'We offer several flexible subscription plans. Please visit our pricing page for current details and features.'
      },
      {
        question: '🆕 What if I want to upload a brand-new app?',
        answer: 'Uploading a new app is not included in either plan. Each new app upload is a one-time service and is priced separately.'
      },
      {
        question: '🆓 Do unused changes roll over?',
        answer: 'No — monthly change limits reset each billing cycle.'
      },
      {
        question: '🔄 Can I switch between plans?',
        answer: 'Yes. You can upgrade or downgrade your plan at any time. Changes take effect from your next billing period.'
      },
      {
        question: '❌ Can I cancel my subscription?',
        answer: 'Yes. Cancel anytime. Your app will remain online until the end of your paid period, and you won\'t be billed again.'
      },
      {
        question: '🧾 Will I receive invoices?',
        answer: 'Yes. Stripe will automatically send you an invoice for every payment.'
      }
    ],
    technical: [
      {
        question: '📱 What platforms do you support?',
        answer: 'We currently support Android apps published via the Google Play Store.'
      },
      {
        question: '📦 What file formats can I upload?',
        answer: 'You can upload:\n\n.AAB (Android App Bundle) files only, as required by Google Play Store.'
      },
      {
        question: '🔗 Can I view my app on the Play Store?',
        answer: 'Yes! Once your app is published, we\'ll give you a direct Play Store link so you can:\n\nSee the full app listing (title, description, screenshots)\n\nVerify the current version'
      },
      {
        question: '📝 Can I update the app listing?',
        answer: 'Yes. You can request updates to:\n\nApp name\n\nDescription\n\nScreenshots\n\nContact info\n\nPrivacy policy URL\nThese count toward your monthly change quota.'
      },
      {
        question: '⏱️ How long do changes take to go live?',
        answer: 'Most updates are processed within 24–48 hours, depending on Play Store review times.'
      },
      {
        question: '🔧 Can I use in-app purchases or permissions?',
        answer: 'Yes — any feature included in your AAB (like billing, permissions, background access) is published as-is. Just make sure it complies with Google\'s policies.'
      },
      {
        question: '🧯 Can you fully delete my app and data?',
        answer: 'Yes. We can unpublish your app from Google Play and permanently remove all data from our systems if you request it.'
      }
    ]
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredFaqs = Object.entries(faqs).reduce((acc, [category, questions]) => {
    const filtered = questions.filter(
      faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  // Generate items for the internal menu in the required format
  const menuItems = Object.entries(categories).map(([id, label]) => ({
    id,
    label
  }));

  return {
    activeCategory,
    setActiveCategory,
    searchQuery,
    faqs,
    filteredFaqs,
    handleSearchChange,
    sidebarItems: menuItems  // Keep the prop name for backwards compatibility
  };
}; 